import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertComponent } from '@components';
import { ServiceRequestStatus } from '@enums';
import { ServiceRequest } from '@models';
import { UtilService } from '@services';
import {
    recreateServiceRequest,
    recreateServiceRequestFailed,
    recreateServiceRequestSuccess,
} from '@state/service-request';

@Component({
  selector: 'app-recreate-service-request-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatInputModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    AlertComponent,
  ],
  templateUrl: './recreate-service-request.dialog.html',
  styleUrl: './recreate-service-request.dialog.scss',
})
export class RecreateServiceRequestDialog {
  private readonly dialogRef = inject(
    MatDialogRef<RecreateServiceRequestDialog>,
  );
  public readonly data = inject(MAT_DIALOG_DATA);

  public form = new FormGroup({
    serviceRequestId: new FormControl('', [Validators.required]),
    customerId: new FormControl('', [Validators.required]),
    orderNumber: new FormControl('', [Validators.required]),
    otp: new FormControl('', [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(4),
    ]),
    weight: new FormControl(0, [Validators.required, Validators.min(0)]),
    volume: new FormControl(0, [Validators.required, Validators.min(0)]),
    packageCount: new FormControl(1, [Validators.required, Validators.min(1)]),
    notes: new FormControl(''),
    pickupStreet: new FormControl('', [Validators.required]),
    pickupStreetNumber: new FormControl('', [Validators.required]),
    pickupSuburb: new FormControl('', [Validators.required]),
    pickupProvince: new FormControl('', [Validators.required]),
    pickupPostalCode: new FormControl('', [Validators.required]),
    pickupCountry: new FormControl('', [Validators.required]),
    pickupLatitude: new FormControl(0, [Validators.required]),
    pickupLongitude: new FormControl(0, [Validators.required]),
    deliveryStreet: new FormControl('', [Validators.required]),
    deliveryStreetNumber: new FormControl('', [Validators.required]),
    deliverySuburb: new FormControl('', [Validators.required]),
    deliveryProvince: new FormControl('', [Validators.required]),
    deliveryPostalCode: new FormControl('', [Validators.required]),
    deliveryCountry: new FormControl('', [Validators.required]),
    deliveryLatitude: new FormControl(0, [Validators.required]),
    deliveryLongitude: new FormControl(0, [Validators.required]),
  });

  public error: string | null = null;
  public loading: boolean = false;

  constructor(private utils: UtilService) {}

  public async onFormSubmit(): Promise<void> {
    if (!this.form.valid) {
      this.error = 'Please fill in all required fields';
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const serviceRequestId = this.getFormControl('serviceRequestId').value!;
      const serviceRequestData: Partial<ServiceRequest> = {
        customerId: this.getFormControl('customerId').value!,
        orderNumber: this.getFormControl('orderNumber').value!,
        otp: this.getFormControl('otp').value!,
        weight: this.getFormControl('weight').value!,
        volume: this.getFormControl('volume').value!,
        packageCount: this.getFormControl('packageCount').value!,
        notes: this.getFormControl('notes').value || undefined,
        status: ServiceRequestStatus.REQUESTED,
        pickupAddress: {
          street: this.getFormControl('pickupStreet').value!,
          streetNumber: this.getFormControl('pickupStreetNumber').value!,
          suburb: this.getFormControl('pickupSuburb').value!,
          province: this.getFormControl('pickupProvince').value!,
          postalCode: this.getFormControl('pickupPostalCode').value!,
          country: this.getFormControl('pickupCountry').value!,
          latitude: this.getFormControl('pickupLatitude').value!,
          longitude: this.getFormControl('pickupLongitude').value!,
        },
        deliveryAddress: {
          street: this.getFormControl('deliveryStreet').value!,
          streetNumber: this.getFormControl('deliveryStreetNumber').value!,
          suburb: this.getFormControl('deliverySuburb').value!,
          province: this.getFormControl('deliveryProvince').value!,
          postalCode: this.getFormControl('deliveryPostalCode').value!,
          country: this.getFormControl('deliveryCountry').value!,
          latitude: this.getFormControl('deliveryLatitude').value!,
          longitude: this.getFormControl('deliveryLongitude').value!,
        },
      };

      await this.utils.actionsToPromise(
        recreateServiceRequest({ serviceRequestId, data: serviceRequestData }),
        recreateServiceRequestSuccess,
        recreateServiceRequestFailed,
      );

      this.dialogRef.close({ success: true });
    } catch (error: any) {
      this.error = this.utils.getErrorMessage(error);
    }
    this.loading = false;
  }

  public getFormControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }
}
